import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a phone number from E164 format to national format
 * French numbers: +33634414576 → 0634414576
 * International numbers: kept as E164
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return "—";
  
  // Remove any spaces
  const cleaned = phone.replace(/\s/g, "");
  
  // Handle French numbers (+33) - replace with 0
  if (cleaned.startsWith("+33")) {
    return "0" + cleaned.slice(3);
  }
  
  // Return as-is for international numbers (keep E164)
  return cleaned;
}

/**
 * Format a date as a human-readable relative string.
 * Recent dates show "Il y a X minutes/heures", older ones show day names or full date.
 *
 * NOTE: `lang` and `timezone` are accepted for forward parity with the
 * Kalyvox helper signature but are currently ignored — the FR output below
 * remains the source of truth for Immovox. Wire them later if/when we
 * formally enable other locales.
 */
export function formatRelativeDate(
  dateString: string,
  _lang?: string,
  _timezone?: string,
): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  // À l'instant (< 1 minute)
  if (diffMinutes < 1) {
    return "À l'instant";
  }
  
  // Il y a X minutes (< 60 minutes)
  if (diffMinutes < 60) {
    return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  }
  
  // Il y a X heures (< 24 heures)
  if (diffHours < 24) {
    return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  }
  
  // Hier à HH:mm
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Hier à ${date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  }
  
  // Cette semaine (< 7 jours) : jour de la semaine
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    const dayName = date.toLocaleDateString("fr-FR", { weekday: "long" });
    const capitalizedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    return `${capitalizedDay} à ${date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  }
  
  // Plus ancien : format classique
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
